import { useState, useEffect, useMemo, useCallback } from 'react';
import { Modal, Button, Input, Checkbox, Table, Form, Switch, message, Tag, Alert } from 'antd';
import { listAllSponsorBenefits, conferenceBenefitList, saveSponsorBenefit, updateSponsorBenefit, deleteSponsorBenefit, batchSaveConferenceBenefit } from '@services/Conference';
import './SelectProjectBenefitsDrawer.less';
import { BENEFIT_CATEGORIES, BENEFIT_CATEGORY_MAP } from '../constants';

/** 权益分类 id 列表（按 BENEFIT_CATEGORIES 顺序） */
const TYPE_KEYS = BENEFIT_CATEGORIES.map(c => c.id);

/** 权益分类 id -> 名称映射（取用 constants 统一数据） */
const TYPE_MAP = BENEFIT_CATEGORY_MAP;

/** 新增/编辑权益弹窗 */
function BenefitFormModal({ open, record, type, typeName, conferenceId, onClose, onSuccess }) {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [_isBooth, setIsBooth] = useState(false);
    const isEdit = !!record;
    // 新增/自定义权益可编辑名称与展位；标化权益（非自定义）仅可编辑权益说明与价格参照
    const canEditBasic = !record || record.builtin === 0;

    useEffect(() => {
        if (open) {
            if (record) {
                setIsBooth(record.isBoothBenefit === 1);
                form.setFieldsValue({
                    benefitName: record.benefitName,
                    isBoothBenefit: record.isBoothBenefit === 1,
                    benefitDesc: record.benefitDesc || '',
                    priceReference: record.priceReference || '',
                });
            } else {
                setIsBooth(false);
                form.resetFields();
            }
        }
    }, [open, record, form]);

    const handleOk = async () => {
        const values = await form.validateFields();
        setLoading(true);

        const param = canEditBasic
            ? {
                  benefitName: values.benefitName,
                  builtin: 0,
                  conferenceId: Number(conferenceId),
                  hidden: 0,
                  id: record?.id || 0,
                  internalName: '',
                  isBoothBenefit: type === 2 && values.isBoothBenefit ? 1 : 0,
                  type,
                  benefitDesc: values.benefitDesc || '',
                  priceReference: values.priceReference || '',
              }
            : {
                  benefitName: record.benefitName,
                  builtin: record.builtin,
                  conferenceId: Number(conferenceId),
                  hidden: record.hidden ?? 0,
                  id: record.id,
                  internalName: record.internalName || '',
                  isBoothBenefit: record.isBoothBenefit ?? 0,
                  type: record.type,
                  benefitDesc: values.benefitDesc || '',
                  priceReference: values.priceReference || '',
              };

        const api = isEdit ? updateSponsorBenefit : saveSponsorBenefit;
        const res = await api(param);
        setLoading(false);

        if (res.status_code === 200) {
            message.success(isEdit ? '编辑成功' : '新增成功');
            onSuccess();
            onClose();
        } else {
            message.error(res.message || (isEdit ? '编辑失败' : '新增失败'));
        }
    };

    return (
        <Modal title={isEdit ? '编辑权益' : '新增权益'} centered open={open} onOk={handleOk} onCancel={onClose} confirmLoading={loading} okText="确认" cancelText="取消" zIndex={1400}>
            <Form form={form} layout="horizontal" labelCol={{ span: 5 }} wrapperCol={{ span: 19 }} autoComplete="off">
                <Form.Item label="权益分类">
                    <span>{typeName}</span>
                </Form.Item>
                {canEditBasic ? (
                    <Form.Item label="权益名称" name="benefitName" rules={[{ required: true, message: '请输入权益名称' }]}>
                        <Input maxLength={200} placeholder="请输入权益名称" showCount />
                    </Form.Item>
                ) : (
                    <Form.Item label="权益名称">
                        <span>{record?.benefitName || '-'}</span>
                    </Form.Item>
                )}
                <Form.Item label="权益说明" name="benefitDesc" extra={canEditBasic ? undefined : <span className="benefit-field-hint">说明参考：{record?.benefitDesc || '-'}</span>}>
                    <Input maxLength={500} showCount placeholder="请输入" />
                </Form.Item>
                <Form.Item label="价格参照" name="priceReference" extra={canEditBasic ? <span className="benefit-field-hint">示例：10000元/次、按每个项目单独确定</span> : <span className="benefit-field-hint">平台大会建议：{record?.priceReference || '-'}，可视本项目会议规模上下浮动</span>}>
                    <Input maxLength={200} showCount placeholder="请输入" />
                </Form.Item>
                {canEditBasic && type === 2 && (
                    <Form.Item label="展位权益" name="isBoothBenefit" valuePropName="checked">
                        <Switch checkedChildren="是" unCheckedChildren="否" onChange={setIsBooth} />
                    </Form.Item>
                )}
            </Form>
        </Modal>
    );
}

/** 单个分类的权益表格 */
function BenefitTypeSection({ type, typeName, benefits, configuredIds, selectedIds, searchText, onSelectChange, onEdit, onAdd, onDelete }) {
    const filtered = useMemo(() => {
        if (!searchText) return benefits;
        const lower = searchText.toLowerCase();
        return benefits.filter(b => b.benefitName?.toLowerCase().includes(lower));
    }, [benefits, searchText]);

    const allSelected = filtered.length > 0 && filtered.every(b => selectedIds.has(b.id));
    const indeterminate = filtered.some(b => selectedIds.has(b.id)) && !allSelected;

    const handleSelectAll = checked => {
        const newIds = new Set(selectedIds);
        filtered.forEach(b => {
            if (configuredIds.has(b.id)) return;
            if (checked) {
                newIds.add(b.id);
            } else {
                newIds.delete(b.id);
            }
        });
        onSelectChange(newIds);
    };

    const columns = [
        {
            title: <Checkbox indeterminate={indeterminate} checked={allSelected} onChange={e => handleSelectAll(e.target.checked)} />,
            width: 50,
            render: (_, record) => {
                const isConfigured = configuredIds.has(record.id);
                const isSelected = selectedIds.has(record.id);
                return (
                    <Checkbox
                        checked={isConfigured || isSelected}
                        disabled={isConfigured}
                        onChange={e => {
                            const newIds = new Set(selectedIds);
                            if (e.target.checked) {
                                newIds.add(record.id);
                            } else {
                                newIds.delete(record.id);
                            }
                            onSelectChange(newIds);
                        }}
                    />
                );
            },
        },
        {
            title: '序号',
            width: 60,
            render: (_, __, index) => index + 1,
        },
        {
            title: '权益名称',
            dataIndex: 'benefitName',
            render: (text, record) => {
                const tags = [];
                if (record.isBoothBenefit === 1) {
                    tags.push(
                        <Tag key="booth" color="orange" size="small">
                            展位权益
                        </Tag>,
                    );
                }
                if (record.builtin === 0) {
                    tags.push(
                        <Tag key="custom" color="blue" size="small">
                            自定义权益
                        </Tag>,
                    );
                }
                return (
                    <div className="benefit-name-cell">
                        <div className="benefit-name-title">
                            {text}
                            {tags.length > 0 && <span style={{ marginLeft: 4 }}>{tags}</span>}
                        </div>
                        <div className="benefit-name-sub">权益说明：{record.benefitDesc || '-'}</div>
                        <div className="benefit-name-sub">价格参照：{record.priceReference || '-'}</div>
                    </div>
                );
            },
        },
        {
            title: '操作',
            width: 120,
            render: (_, record) => {
                const isCustom = record.builtin === 0;
                const isConfigured = configuredIds.has(record.id);
                return (
                    <span>
                        <Button type="link" size="small" onClick={() => onEdit(record, type)}>
                            编辑
                        </Button>
                        {isCustom && (
                            <Button type="link" size="small" danger disabled={isConfigured} onClick={() => onDelete(record)}>
                                删除
                            </Button>
                        )}
                    </span>
                );
            },
        },
    ];

    return (
        <div className="benefit-type-section">
            <div className="benefit-type-header">
                <span className="benefit-type-name">{typeName}</span>
                <Button type="link" size="small" onClick={() => onAdd(type)}>
                    <i className="bi bi-plus-circle" /> 新增权益
                </Button>
            </div>
            {type === 4 && <div className="benefit-type-tip">仅“论坛分类”中的权益，可关联具体论坛。</div>}
            {filtered.length === 0 ? <div className="benefit-type-empty">暂无配置权益</div> : <Table className="benefit-select-table" columns={columns} dataSource={filtered} rowKey="id" pagination={false} size="small" bordered />}
        </div>
    );
}

export default function SelectProjectBenefitsDrawer({ open, onClose, conferenceId, onSave }) {
    const [allBenefits, setAllBenefits] = useState([]);
    const [configuredIds, setConfiguredIds] = useState(new Set());
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [searchText, setSearchText] = useState('');
    const [_loading, setLoading] = useState(false);
    const [formModalOpen, setFormModalOpen] = useState(false);
    const [formRecord, setFormRecord] = useState(null);
    const [formType, setFormType] = useState(1);

    const originalSelectedIds = useMemo(() => new Set([...configuredIds]), [configuredIds]);

    // 按类型分组，并按正序排列（createdAt 有效时按其升序，缺失/相同则以 id 升序兜底）
    const groupedBenefits = useMemo(() => {
        const groups = {};
        TYPE_KEYS.forEach(key => {
            groups[key] = [];
        });
        allBenefits.forEach(item => {
            const type = item.type;
            if (groups[type]) {
                groups[type].push(item);
            }
        });
        const timeOf = b => {
            const t = b.createdAt ? new Date(b.createdAt).getTime() : NaN;
            return Number.isNaN(t) ? null : t;
        };
        TYPE_KEYS.forEach(key => {
            groups[key].sort((a, b) => {
                const ta = timeOf(a);
                const tb = timeOf(b);
                if (ta !== null && tb !== null && ta !== tb) return ta - tb;
                return (a.id || 0) - (b.id || 0);
            });
        });
        return groups;
    }, [allBenefits]);

    const hasChanged = useMemo(() => {
        if (selectedIds.size !== originalSelectedIds.size) return true;
        for (const id of selectedIds) {
            if (!originalSelectedIds.has(id)) return true;
        }
        for (const id of originalSelectedIds) {
            if (!selectedIds.has(id)) return true;
        }
        return false;
    }, [selectedIds, originalSelectedIds]);

    // 获取数据
    const fetchData = useCallback(async () => {
        if (!conferenceId) return;
        setLoading(true);

        const [allRes, configRes] = await Promise.all([listAllSponsorBenefits({ conferenceId: Number(conferenceId) }), conferenceBenefitList({ conferenceId: Number(conferenceId) })]);

        let newAllBenefits = [];
        if (allRes.status_code === 200) {
            newAllBenefits = allRes.data || [];
            setAllBenefits(newAllBenefits);
        }

        if (configRes.status_code === 200) {
            const configList = configRes.data || [];
            const ids = new Set(configList.map(item => item.benefitId));
            setConfiguredIds(ids);
            // 保留用户之前勾选的权益（排除已配置 + 仍存在于新数据中的）
            setSelectedIds(prev => {
                const newIds = new Set(ids);
                prev.forEach(id => {
                    if (!ids.has(id) && newAllBenefits.some(b => b.id === id)) {
                        newIds.add(id);
                    }
                });
                return newIds;
            });
        }

        setLoading(false);
        return newAllBenefits;
    }, [conferenceId]);

    useEffect(() => {
        if (open && conferenceId) {
            fetchData();
        }
    }, [open, conferenceId, fetchData]);

    const handleAdd = type => {
        setFormRecord(null);
        setFormType(type);
        setFormModalOpen(true);
    };

    const handleEdit = (record, type) => {
        setFormRecord(record);
        setFormType(type);
        setFormModalOpen(true);
    };

    const handleDelete = record => {
        Modal.confirm({
            title: '提示',
            content: '确认删除该自定义权益',
            okText: '确认',
            cancelText: '取消',
            onOk: async () => {
                const res = await deleteSponsorBenefit({ id: record.id });
                if (res.status_code === 200) {
                    message.success('删除成功');
                    fetchData();
                } else {
                    message.error(res.message || '删除失败');
                }
            },
        });
    };

    const handleSave = async () => {
        // 只传新选的权益（排除已配置的）
        const newBenefitIds = [...selectedIds].filter(id => !configuredIds.has(id));
        const benefitMap = new Map(allBenefits.map(b => [b.id, b]));
        const dtoList = newBenefitIds.map(benefitId => {
            const benefit = benefitMap.get(benefitId);
            return {
                benefitId,
                conferenceId: Number(conferenceId),
                id: 0,
                quantity: 0,
                boothNumbers: null,
                benefitDesc: benefit?.benefitDesc || '',
                priceReference: benefit?.priceReference || '',
            };
        });

        const res = await batchSaveConferenceBenefit(dtoList);

        if (res.status_code === 200) {
            message.success('保存成功');
            onSave?.(selectedIds);
            onClose();
        } else {
            message.error(res.message || '保存失败');
        }
    };

    const handleClose = () => {
        if (hasChanged) {
            Modal.confirm({
                title: '提示',
                content: '发现有数据变动，确认不保存并关闭？',
                okText: '确认',
                cancelText: '取消',
                onOk: () => {
                    setSelectedIds(originalSelectedIds);
                    onClose();
                },
            });
        } else {
            onClose();
        }
    };

    return (
        <>
            <Modal
                title="选择项目权益"
                width={{
                    sx: '100%',
                    md: '80%',
                    lg: '80%',
                    xl: 800,
                }}
                open={open}
                onCancel={handleClose}
                className="select-project-benefits-modal"
                destroyOnHidden
                closable={false}
                styles={{ body: { padding: 0, height: 600, display: 'flex', flexDirection: 'column', overflow: 'hidden' } }}
                footer={
                    <div className="select-benefits-footer">
                        <Button onClick={handleClose}>取消</Button>
                        <Button type="primary" onClick={handleSave}>
                            保存
                        </Button>
                    </div>
                }
            >
                <div className="select-benefits-content">
                    <Alert type="warning" title="请按本项目“商务函”中的权益进行选择" />

                    {/* 搜索框 */}
                    <div className="select-benefits-search">
                        <Input placeholder="输入权益关键字" value={searchText} onChange={e => setSearchText(e.target.value)} allowClear prefix={<i className="bi bi-search" />} />
                    </div>

                    {/* 按分类展示 */}
                    <div className="select-benefits-list">
                        {TYPE_KEYS.map(type => (
                            <BenefitTypeSection key={type} type={type} typeName={TYPE_MAP[type]} benefits={groupedBenefits[type] || []} configuredIds={configuredIds} selectedIds={selectedIds} searchText={searchText} onSelectChange={setSelectedIds} onEdit={handleEdit} onAdd={handleAdd} onDelete={handleDelete} />
                        ))}
                    </div>
                </div>
            </Modal>

            <BenefitFormModal
                open={formModalOpen}
                record={formRecord}
                type={formType}
                typeName={TYPE_MAP[formType]}
                conferenceId={conferenceId}
                onClose={() => {
                    setFormModalOpen(false);
                    setFormRecord(null);
                }}
                onSuccess={async () => {
                    const beforeIds = new Set(allBenefits.map(b => b.id));
                    const newAllBenefits = await fetchData();
                    const newIds = (newAllBenefits || []).filter(b => !beforeIds.has(b.id) && b.builtin === 0).map(b => b.id);
                    if (newIds.length > 0) {
                        setSelectedIds(prev => new Set([...prev, ...newIds]));
                    }
                }}
            />
        </>
    );
}
